import React, { cloneElement, useCallback } from 'react';
import type { FunctionComponent, ReactElement } from 'react';

import { BINARY_DECOMPOSITION, VENDORED_DEPENDENCY_DETECTION } from '../../../../shared/featureFlags/flagNames';
import { useLocation, useParams } from '../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../helpers/features';
import SidebarNavLayout from '../../layouts/SidebarNavLayout';
import type { ReleaseGroupRouteParams } from '../project_grouping/types';

/**
 * ReleaseGroupInventoryBrowseContainer - When the Inventory UI FF is enabled, this will display the sidebar nav for
 * both the Dependencies, Snippets, and Binary Analysis (now grouped together as "Inventory").
 */
const ReleaseGroupInventoryBrowseContainer: FunctionComponent = React.memo(({ children, ...props }) => {
  const params = useParams<ReleaseGroupRouteParams>();
  const location = useLocation();
  const isBinaryDecompositionEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);
  const isVendoredEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);

  const baseUrl = `/projects/group/${params.projectGroupId}`;
  const isPathActive = useCallback((pathPart: 'dependencies' | 'snippets' | 'binary' | 'vendored') => {
    const pathWithoutTrailingSlash = location.pathname.replace(/\/$/, '');
    return pathWithoutTrailingSlash.endsWith(pathPart);
  }, [location.pathname]);

  return (
    <SidebarNavLayout>
      <SidebarNavLayout.Nav>
        <SidebarNavLayout.NavItem
          to={`${baseUrl}/browse/dependencies`}
          isActive={isPathActive('dependencies')}
        >
          Dependencies
        </SidebarNavLayout.NavItem>
        {isVendoredEnabled && (
          <SidebarNavLayout.NavItem
            to={`${baseUrl}/browse/vendored`}
            isActive={isPathActive('vendored')}
          >
            Vendored
          </SidebarNavLayout.NavItem>
        )}
        {isBinaryDecompositionEnabled && (
          <SidebarNavLayout.NavItem
            to={`${baseUrl}/browse/binary`}
            isActive={isPathActive('binary')}
          >
            Binary Analysis
          </SidebarNavLayout.NavItem>
        )}
      </SidebarNavLayout.Nav>
      <SidebarNavLayout.Content>
        {cloneElement(children as ReactElement, { ...props, scope: 'releaseGroup' })}
      </SidebarNavLayout.Content>
    </SidebarNavLayout>
  );
});

export default ReleaseGroupInventoryBrowseContainer;
